import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  FileText, 
  Download, 
  Search, 
  Calendar,
  Activity,
  Users,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Filter,
  RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [constituencies, setConstituencies] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 25;
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    entity_type: '',
    action: '',
    constituency_id: '',
    campaign_id: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    loadConstituencies();
    loadStats();
    // Run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), page_size: pageSize.toString() });
      if (filters.search) params.append('search', filters.search);
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.action) params.append('action', filters.action);
      if (filters.constituency_id) params.append('constituency_id', filters.constituency_id);
      if (filters.campaign_id) params.append('campaign_id', filters.campaign_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      const response = await axios.get(`${API}/audit-logs?${params}`, { withCredentials: true });
      setLogs(response.data.logs || []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    if (filters.constituency_id) {
      loadCampaigns(filters.constituency_id);
    } else {
      setCampaigns([]);
    }
  }, [filters.constituency_id]);

  const loadConstituencies = async () => {
    try {
      const response = await axios.get(`${API}/constituencies`, { withCredentials: true });
      setConstituencies(response.data);
    } catch (error) {
      console.error('Failed to load constituencies');
    }
  };

  const loadCampaigns = async (constituencyId) => {
    try {
      const response = await axios.get(`${API}/campaigns?constituency_id=${constituencyId}`, { withCredentials: true });
      setCampaigns(response.data);
    } catch (error) {
      console.error('Failed to load campaigns');
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/audit-logs/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.constituency_id) params.append('constituency_id', filters.constituency_id);
      if (filters.campaign_id) params.append('campaign_id', filters.campaign_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const response = await axios.get(`${API}/audit-logs/export?${params}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'audit_logs_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Audit logs exported');
    } catch (error) {
      toast.error('Failed to export audit logs');
    }
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      entity_type: '',
      action: '',
      constituency_id: '',
      campaign_id: '',
      start_date: '',
      end_date: ''
    });
    setPage(1);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'update': return <Edit className="h-4 w-4 text-blue-500" />;
      case 'delete': return <Trash2 className="h-4 w-4 text-red-500" />;
      case 'create': return <FileText className="h-4 w-4 text-green-500" />;
      default: return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  const getActionBadge = (action) => {
    const colors = {
      update: 'bg-blue-100 text-blue-700',
      delete: 'bg-red-100 text-red-700',
      create: 'bg-green-100 text-green-700'
    };
    return colors[action] || 'bg-gray-100 text-gray-700';
  };

  return (
    <Layout>
      <div className="p-4 md:p-8" data-testid="audit-log-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Audit Log
            </h1>
            <p className="text-muted-foreground">Track all changes made to constituent records</p>
          </div>
          <Button onClick={handleExport} className="mt-4 md:mt-0" data-testid="export-audit-logs">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Activity className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Logs</p>
                    <p className="text-2xl font-bold">{stats.total_logs.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Edit className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Updates</p>
                    <p className="text-2xl font-bold">{(stats.by_action?.update || 0).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Trash2 className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Deletes</p>
                    <p className="text-2xl font-bold">{(stats.by_action?.delete || 0).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Users className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Active Users</p>
                    <p className="text-2xl font-bold">{(stats.top_users?.length || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label>Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search names, values..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="pl-10"
                    data-testid="audit-search-input"
                  />
                </div>
              </div>
              
              <div>
                <Label>Action</Label>
                <Select 
                  value={filters.action} 
                  onValueChange={(v) => setFilters({ ...filters, action: v === '__all__' ? '' : v })}
                >
                  <SelectTrigger data-testid="audit-action-filter">
                    <SelectValue placeholder="All Actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Actions</SelectItem>
                    <SelectItem value="update">Update</SelectItem>
                    <SelectItem value="delete">Delete</SelectItem>
                    <SelectItem value="create">Create</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Constituency</Label>
                <Select 
                  value={filters.constituency_id} 
                  onValueChange={(v) => setFilters({ ...filters, constituency_id: v === '__all__' ? '' : v, campaign_id: '' })}
                >
                  <SelectTrigger data-testid="audit-constituency-filter">
                    <SelectValue placeholder="All Constituencies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Constituencies</SelectItem>
                    {constituencies.map(c => (
                      <SelectItem key={c.constituency_id} value={c.constituency_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Campaign</Label>
                <Select 
                  value={filters.campaign_id} 
                  onValueChange={(v) => setFilters({ ...filters, campaign_id: v === '__all__' ? '' : v })}
                  disabled={!filters.constituency_id}
                >
                  <SelectTrigger data-testid="audit-campaign-filter">
                    <SelectValue placeholder="All Campaigns" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Campaigns</SelectItem>
                    {campaigns.map(c => (
                      <SelectItem key={c.campaign_id} value={c.campaign_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  data-testid="audit-start-date"
                />
              </div>
              
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  data-testid="audit-end-date"
                />
              </div>
              
              <div className="flex items-end">
                <Button variant="outline" onClick={resetFilters} className="w-full">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reset
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : logs.length === 0 ? (
              <div className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No audit logs found</h3>
                <p className="text-muted-foreground">No changes have been recorded yet or no logs match your filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>Field</TableHead>
                      <TableHead>Old Value</TableHead>
                      <TableHead>New Value</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Type</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.log_id} data-testid="audit-log-row">
                        <TableCell className="whitespace-nowrap text-sm">
                          {formatTimestamp(log.timestamp)}
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${getActionBadge(log.action)}`}>
                            {getActionIcon(log.action)}
                            {log.action}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{log.entity_name || log.entity_id}</p>
                            <p className="text-xs text-muted-foreground">{log.entity_type}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {log.field_changed || '-'}
                        </TableCell>
                        <TableCell className="text-sm max-w-[150px] truncate" title={log.old_value}>
                          {log.old_value || '-'}
                        </TableCell>
                        <TableCell className="text-sm max-w-[150px] truncate" title={log.new_value}>
                          {log.new_value || '-'}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{log.user_name || 'Unknown'}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs ${
                            log.user_type === 'caller' 
                              ? 'bg-orange-100 text-orange-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {log.user_type}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} of {total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
